/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// API REST Client para Cloudflare D1 via Worker
// Em desenvolvimento local, usa proxy do Vite. Em produção, usa URL do Worker
const API_BASE = import.meta.env.PROD 
  ? 'https://skyhost.santorangoexpress.workers.dev/api'
  : '/api';

// Error Handling Infrastructure
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface ApiErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): void {
  const errInfo: ApiErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    operationType,
    path
  };
  console.warn(`API Error [${operationType}] on ${path}:`, errInfo.error);
}

// Helper para fazer requisições à API
export async function apiRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Test connection
export async function testConnection() {
  try {
    await apiRequest('/health');
    console.log('API connection successful');
  } catch (error) {
    console.error('API connection failed:', error);
  }
}

// Bootstrap function: If the database is empty, fill it with pre-populated data
export async function bootstrapDatabaseIfEmpty(
  defaultHotels: any[],
  defaultClients: any[],
  defaultSpots: any[],
  defaultTypes: any[],
  defaultAccs: any[],
  defaultCats: any[],
  defaultItems: any[],
  defaultMinibars: any[],
  defaultReservations: any[],
  defaultProfile: any
) {
  try {
    const hotels = await apiRequest<any[]>('/hotels');
    if (hotels.length === 0) {
      console.log('Database is empty. Bootstrapping initial dataset...');
      
      await apiRequest('/seed', {
        method: 'POST',
        body: JSON.stringify({
          profile: defaultProfile,
          hotels: defaultHotels,
          clients: defaultClients,
          parkingSpots: defaultSpots,
          accommodationTypes: defaultTypes,
          accommodations: defaultAccs,
          stockCategories: defaultCats,
          stockItems: defaultItems,
          minibars: defaultMinibars,
          reservations: defaultReservations,
        }),
      });

      console.log('Bootstrapping completed successfully.');
    }
  } catch (error) {
    console.error('Error during bootstrapping:', error);
  }
}

// Generic CRUD Operations
export async function dbSetDoc(col: string, docId: string, data: any): Promise<boolean> {
  try {
    // Map collection names to API endpoints
    const endpointMap: Record<string, string> = {
      'profile': '/profile',
      'hotels': '/hotels',
      'clients': '/clients',
      'parkingSpots': '/parking',
      'accommodationTypes': '/accommodation-types',
      'accommodations': '/accommodations',
      'stockCategories': '/stock-categories',
      'stockItems': '/stock-items',
      'minibars': '/minibars',
      'reservations': '/reservations',
    };

    const endpoint = endpointMap[col];
    if (!endpoint) {
      console.warn(`[SkyHost] Unknown collection: ${col}`);
      return false;
    }

    const body = JSON.stringify({ id: docId, ...data });
    console.log(`[SkyHost] POST ${API_BASE}${endpoint}`, body.substring(0, 200));
    
    const result = await apiRequest(`${endpoint}`, {
      method: 'POST',
      body,
    });
    
    console.log(`[SkyHost] POST OK for ${col}/${docId}`);
    return true;
  } catch (error) {
    console.error(`[SkyHost] POST FAILED for ${col}/${docId}:`, error);
    handleFirestoreError(error, OperationType.WRITE, `${col}/${docId}`);
    return false;
  }
}

export async function dbUpdateDoc(col: string, docId: string, data: any): Promise<boolean> {
  try {
    const endpointMap: Record<string, string> = {
      'profile': '/profile',
      'hotels': '/hotels',
      'clients': '/clients',
      'parkingSpots': '/parking',
      'accommodationTypes': '/accommodation-types',
      'accommodations': '/accommodations',
      'stockCategories': '/stock-categories',
      'stockItems': '/stock-items',
      'minibars': '/minibars',
      'reservations': '/reservations',
    };

    const endpoint = endpointMap[col];
    if (!endpoint) {
      console.warn(`Unknown collection: ${col}`);
      return false;
    }

    await apiRequest(`${endpoint}/${docId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return true;
  } catch (error) {
    handleFirestoreError(error, OperationType.UPDATE, `${col}/${docId}`);
    return false;
  }
}

export async function dbDeleteDoc(col: string, docId: string): Promise<boolean> {
  try {
    const endpointMap: Record<string, string> = {
      'clients': '/clients',
      'parkingSpots': '/parking',
    };

    const endpoint = endpointMap[col];
    if (!endpoint) {
      console.warn(`Delete not supported for collection: ${col}`);
      return false;
    }

    await apiRequest(`${endpoint}/${docId}`, {
      method: 'DELETE',
    });
    return true;
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, `${col}/${docId}`);
    return false;
  }
}

// Export collection function for compatibility
export { apiRequest as collection };

// Export getDocs as a function that fetches from API
export async function getDocs(endpoint: string) {
  return apiRequest(endpoint);
}
